import {
  Component,
  Inject,
  EventEmitter,
  Output,
  ElementRef,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ILoginResponse, IResponse } from '@interfaces/index';
import { environment } from '@environments/environment';
import { AppService } from '../services/app.service';
import { AuthService } from '../services/auth.service';
import { OktaService } from '../../../../ui-components/src/services/okta.service';
import { ConsentService } from '@ui-components/consent';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent {
  form: FormGroup;
  loading = false;
  login_invalid = false;
  readonly showOkta = !environment.production;
  @Output() isIntersecting = new EventEmitter<boolean>();
  private observer: IntersectionObserver | null = null;


  private clearedStaleOktaState: Promise<void> = Promise.resolve();

  constructor(
    protected _fb: FormBuilder,
    protected _app_service: AppService,
    protected _auth_service: AuthService,
    protected oktaService: OktaService,
    private route: ActivatedRoute,
    private el: ElementRef,
    public consent: ConsentService
  ) {
    this.form = this._fb.group({
      email: [null, Validators.required],
      password: [null, Validators.required]
    });
  }

  ngOnInit(): void {
    // Landing here right after an explicit logout — this origin's own Okta
    // token cache (localStorage) is separate from whichever app the logout
    // was triggered from, so it can still hold a stale, unexpired token that
    // would otherwise silently re-authenticate the user via checkOktaLogin().
    if (this.route.snapshot.queryParamMap.get('logout') === '1') {
      this.clearedStaleOktaState = Promise.resolve()
        .then(() => this.oktaService.oktaAuth.tokenManager.clear())
        .catch(() => undefined);
    }

    this._watchElementForEntry();
  }

  private _watchElementForEntry(): void {
    const options: IntersectionObserverInit = {
      root: null, // defaults to the browser viewport
      rootMargin: '0px',
      threshold: 0.1, // trigger when 10% of the element is visible
    };

    const callback = (entries: IntersectionObserverEntry[]): void => {
      entries.forEach(entry => {
        this.isIntersecting.emit(entry.isIntersecting);

        if (entry.isIntersecting) {
          this.clearedStaleOktaState
            .then(() => this.oktaService.setAuthState())
            .then(() => this.checkOktaLogin())
            .then(() => {
              this.observer?.unobserve(entry.target);
            });
        }
      });
    };

    this.observer = new IntersectionObserver(callback, options);
    this.observer.observe(this.el.nativeElement);
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect(); // clean up the observer to prevent memory leaks
    }
  }

  login() {
    if (this.form.valid) {
      this.setLoadingState(true);

      const email = this.form.get('email')?.value;
      const password = this.form.get('password')?.value;

      this._auth_service.login(email, password).subscribe({
        next:(response: IResponse<ILoginResponse>) => {
          this._app_service.handleLoginResponse(response);
          this.login_invalid = response.code !== 200;
          this.setLoadingState(false); 
        },
        error: () => {
          this._app_service.handleLoginError();
          this.setLoadingState(false); 
        }
      });
    }
  }

  async login2() {
    await this.setLoadingState(true);
    // get user and org from DB by email address
    await this.oktaService.setAuthState();
    if (await this.oktaService.getIsAuthenticated()) {
      await this.oktaService.loginUserFromOkta(this.oktaService.handleLoginResponse);
      this.setLoadingState(false);
    }

    await this.oktaService.signIn();
  }

  async checkOktaLogin() {
    if (this.oktaService.getIsAuthenticated()) {
      await this.oktaService.loginUserFromOkta(this.oktaService.handleLoginResponse);
    }
  }

  oktaLogin() {
    window.location.href = '/okta';
  }

  setLoadingState(state: boolean) {
    this.loading = state;
  }
}
